import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import Heading from '@atoms/Heading';
import HeadingSize from '@atoms/Heading/sizes';
import ButtonType from '@atoms/Button/type';
import { ButtonProps } from '@atoms/Button';
import LoadingBox from '@atoms/LoadingBox';
import GrayContainer from '@atoms/GrayContainer';
import Info from '@atoms/Info';

import ToggleButtons from '@molecules/ToggleButtons';
import Input from '@molecules/Input';
import NextPrevButtons from '@molecules/NextPrevButtons';
import Selector from '@molecules/Selector';
import Veil from '@molecules/Veil';

import {
  CONVERSION_RATE,
  MAX_LOAD_HEIGHT_INCHES,
  MAX_LOAD_HEIGHT_MM,
} from 'config/calculator';

import step3Styles from '@templates/Step3/styles';

import useCalculatorState from 'hooks/useCalculatorState';
import useLayoutConfigState from 'hooks/useLayoutConfigState';
import useImagesState from 'hooks/useImagesState';
import useBreakpoint from 'hooks/useBreakpoint';
import useLayerAlignmentState from 'hooks/useLayerAligmentState';

import { LayerAlignmentType, LoadHeightType, UnitDimension } from 'enums/calculator';
import { convertTo } from 'utils/numbers';

import styles from './styles';

const LazyComponent = React.lazy(() => import('@organisms/Pallet3D/Pallet3D'));

const Step4 = () => {
  const { t } = useTranslation();
  const {
    dimensions,
    height,
    palletData,
    isStep4Valid,
    setLoadHeightType,
    setLoadHeightUnit,
    setLoadHeightValue,
  } = useCalculatorState();

  const {
    isSaving,
    save3DImage,
    on3DPalletReady,
  } = useImagesState();

  const {
    nextStep,
  } = useCalculatorState();

  const {
    isMobile,
  } = useBreakpoint();

  const {
    alignmentActiveItem,
  } = useLayerAlignmentState();
  const currentAlignmentType = (
    alignmentActiveItem && alignmentActiveItem.id
  ) || LayerAlignmentType.PARALLEL;

  const {
    activeItem,
  } = useLayoutConfigState();
  const { alignmentItemsButtons, setLayerAlignmentItem } = useLayerAlignmentState();

  const loadHeightTypeButtons: ButtonProps[] = [
    {
      type: ButtonType.PILL,
      children: LoadHeightType.HEIGHT,
      active: height.type === LoadHeightType.HEIGHT,
      className: styles.toggleButton1,
    },
    {
      type: ButtonType.PILL,
      children: LoadHeightType.LAYERS,
      active: height.type === LoadHeightType.LAYERS,
      className: styles.toggleButton2,
    },
  ];

  const unitOptions: ButtonProps[] = [
    {
      type: ButtonType.PILL,
      children: UnitDimension.MM,
      active: height.unit === UnitDimension.MM,
      className: styles.toggleUnitButton1,
    },
    {
      type: ButtonType.PILL,
      children: UnitDimension.INCH,
      active: height.unit === UnitDimension.INCH,
      className: styles.toggleUnitButton2,
    },
  ];

  const {
    error: heightError,
    warning: heightWarning,
    value: heightValue,
    layers,
  } = height || {};

  const {
    height: packageHeight,
    type: packageUnit,
  } = dimensions;

  const unitLoadHeightChange = (item: ButtonProps) => setLoadHeightType(
    item.children as LoadHeightType,
  );
  const unitLoadHeightUnitChange = (item: string) => setLoadHeightUnit(
    item as UnitDimension,
  );

  const dimensionsSelector = (
    <Selector
      items={unitOptions}
      onChange={unitLoadHeightUnitChange}
      selected={height.unit}
    />
  );

  const loadHeightChange = (newValue: number) => setLoadHeightValue(newValue);
  const defaultMax = height.unit === UnitDimension.MM ? MAX_LOAD_HEIGHT_MM : MAX_LOAD_HEIGHT_INCHES;
  let packageHeightValue = packageHeight;
  if (packageUnit !== height.unit) {
    packageHeightValue = convertTo(Number(packageHeight), height.unit);
  }
  const currentHeight = heightValue || packageHeightValue;
  let finalMax = defaultMax;
  let palletHeight = 0;

  if (palletData.height) {
    if (height.unit === UnitDimension.MM) {
      palletHeight = palletData.unitType === UnitDimension.INCH
        ? Math.floor(Number(palletData.height) * CONVERSION_RATE)
        : Math.floor(Number(palletData.height));
    } else {
      palletHeight = palletData.unitType === UnitDimension.MM
        ? Math.floor(Number(palletData.height) / CONVERSION_RATE)
        : Math.floor(Number(palletData.height));
    }
    finalMax -= palletHeight;
  }

  const maxLayers = Math.floor(finalMax / Number(packageHeightValue));
  const placeholder = height.type === LoadHeightType.LAYERS ? `max:${maxLayers}*` : dimensionsSelector;

  const heightInputValue = (height.type === LoadHeightType.LAYERS ? layers : currentHeight) || 0;
  const heightUnitString = t(String(height.unit).toLowerCase(), { count: heightInputValue });

  const heightInfo = `${(currentHeight || 0) + palletHeight} ${heightUnitString.toLowerCase()}`;
  const heightDescription = height.type === LoadHeightType.LAYERS ? `The total height including the pallet would be ${heightInfo}` : `The pallet height is ${palletHeight} ${height.unit.toLowerCase()}.`;

  const error = heightError || heightWarning;
  const savingImagesComponent = isSaving && <Veil message={t('Generating report...')} />;
  /**
   * Save 3D Image before continue to the next step
   */
  const onNextButtonClick = () => {
    save3DImage().then(() => {
      nextStep();
    });
  };

  const onLayerAlignmentChange = (e:ButtonProps) => setLayerAlignmentItem(e.data);

  const canGoNextStep = isStep4Valid() && !error;
  const INFO_MESSAGE = '* The max number of layers is calculated based off of our predetermined max load height of 110 inches.';
  const layersToShow = layers > 0 && layers < maxLayers ? layers : maxLayers;

  return (
    <section className="md:flex justify-between md:gap-12 text-lg items-start">
      <section className="user-input md:w-2/5">
        <section id="pallet-data">
          <Heading size={HeadingSize.H1}>
            {t('home:step4.title')}
          </Heading>
          <p>{t('home:step4.description')}</p>
          <Info message={INFO_MESSAGE} />
          <div className={styles.toggleSection}>
            <Heading>{t('Load height')}</Heading>
            <ToggleButtons
              className={styles.typeToggle}
              items={loadHeightTypeButtons}
              onChange={unitLoadHeightChange}
            />
          </div>
          <div>
            <Input
              id="weight"
              type="number"
              onChange={loadHeightChange}
              value={String(heightInputValue) || ''}
              error={error}
              step="01"
              placeholder={placeholder}
            />
            <p className="block text-darkGray text-xs mt-1">{ heightDescription }</p>
          </div>
          <article className={styles.section}>
            <Heading>{t('Alignment')}</Heading>
            <ToggleButtons
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4"
              items={alignmentItemsButtons}
              onChange={onLayerAlignmentChange}
            />
          </article>
        </section>
        {
          !isMobile
          && (
            <NextPrevButtons
              nextButtonEnabled={canGoNextStep}
              onNextButtonClick={onNextButtonClick}
            />
          )
        }
      </section>
      <section className="my-14 md:my-0 md:w-3/5">
        <GrayContainer className={step3Styles.grayContainerClasses}>
          {savingImagesComponent}
          <Suspense fallback={<LoadingBox border={false} bigText className="text-mainBlue">{t('Loading 3D model data...')}</LoadingBox>}>
            <LazyComponent
              activePattern={activeItem}
              view3D
              layers={layersToShow}
              onReady={on3DPalletReady}
              alignmentType={currentAlignmentType}
            />
          </Suspense>
        </GrayContainer>
        <small className="block text-darkGray text-xs mt-5 leading-[18px]">
          <p className="!mb-0">The rendering is theoretical and may not accurately reflect reality.</p>
          <p className="!mb-0">We make no warranties or representations as to its accuracy</p>
        </small>
      </section>
      {
        isMobile
        && (
          <NextPrevButtons
            nextButtonEnabled={canGoNextStep}
            onNextButtonClick={onNextButtonClick}
          />
        )
      }
    </section>
  );
};

export default Step4;
